import {useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import moment from "moment";
import {
	Area,
	CartesianGrid,
	ComposedChart,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

import {PlayerAPI} from "@/api/player";
import {PlayerValuationAPI} from "@/api/player_valuation";
import SmartRadarChart from "@/components/SmartRadarChart";
import {apiFieldsToValues} from "@/config/smartValuationConfig";

import "./index.scss";

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (v, d = 1) => (v != null && !isNaN(v) ? Number(v).toFixed(d) : "—");

function getWyscoutPlayerData(playerData) {
	if (!playerData) return null;
	try {
		const saved = localStorage.getItem("wyscout_player_stats");
		if (!saved) return null;
		const imports = JSON.parse(saved);
		const lastName = (playerData.lastName || "").toLowerCase().trim();
		return imports.find((p) =>
			p.playerName?.toLowerCase().includes(lastName) && lastName.length > 2,
		) || null;
	} catch {
		return null;
	}
}

const ROLE_LABEL = {
	Portiere: "GK", Difensore: "DEF", Mediano: "MED",
	Centrocampista: "CEN", Trequartista: "TRE", Attaccante: "ATT",
};

// ── Component ─────────────────────────────────────────────────────────────────

function PublicPlayerPage() {
	const {id} = useParams();

	const [loading, setLoading] = useState(true);
	const [player, setPlayer] = useState(null);
	const [valuations, setValuations] = useState([]);
	const [error, setError] = useState(false);

	useEffect(() => {
		const load = async () => {
			try {
				// Prova endpoint pubblico, fallback a getById (funziona se loggato)
				let data;
				try {
					data = await PlayerAPI.getPublic(id);
				} catch {
					data = await PlayerAPI.getById(id);
				}
				setPlayer(data);

				// Valutazioni
				try {
					const vals = await PlayerValuationAPI.playerList(id);
					setValuations(vals || []);
				} catch {
					setValuations([]);
				}
			} catch {
				setError(true);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [id]);

	// ── Derived data ──────────────────────────────────────────────────────────

	const age = player?.dateOfBirth
		? moment().diff(moment(player.dateOfBirth), "years")
		: player?.birthDate
			? moment().diff(moment(player.birthDate), "years")
			: null;

	const wyscoutData = useMemo(() => getWyscoutPlayerData(player), [player]);

	// Growth data
	const heightData = useMemo(() => {
		return (player?.playersGrowth || [])
			.filter((g) => g.height && g.date)
			.map((g) => ({
				label: moment(g.date).format("MMM YY"),
				value: parseFloat(g.height),
			}))
			.sort((a, b) => new Date(a.date) - new Date(b.date));
	}, [player?.playersGrowth]);

	const weightData = useMemo(() => {
		return (player?.playersGrowth || [])
			.filter((g) => g.weight && g.date)
			.map((g) => ({
				label: moment(g.date).format("MMM YY"),
				value: parseFloat(g.weight),
			}))
			.sort((a, b) => new Date(a.date) - new Date(b.date));
	}, [player?.playersGrowth]);

	// Valuation average
	const avgRating = useMemo(() => {
		const ratings = valuations
			.map((v) => parseFloat(v.finalOverallRating || v.averageRating || v.rating))
			.filter((v) => !isNaN(v));
		if (!ratings.length) return null;
		return ratings.reduce((s, v) => s + v, 0) / ratings.length;
	}, [valuations]);

	// ── Render ────────────────────────────────────────────────────────────────

	if (loading) {
		return (
			<div className="pp-loading">
				<div className="pp-loading__spinner" />
			</div>
		);
	}

	if (error || !player) {
		return (
			<div className="pp-error">
				<h2>Profilo non disponibile</h2>
				<p>Il link potrebbe essere scaduto o il giocatore non e piu nel sistema.</p>
			</div>
		);
	}

	const roleShort = player.goalkeeper ? "GK" : (ROLE_LABEL[player.primaryRole] || "");

	return (
		<div className="pp">
			{/* ── Header ──────────────────────────────────────── */}
			<div className="pp__header">
				<div className="pp__header-brand">
					<span className="pp__header-logo">⚽</span>
					<span className="pp__header-title">Player Portfolio</span>
				</div>
			</div>

			{/* ── Profile card ────────────────────────────────── */}
			<div className="pp__profile">
				<div className="pp__profile-main">
					<div className="pp__profile-avatar">
						{roleShort || "?"}
					</div>
					<div className="pp__profile-info">
						<h1 className="pp__profile-name">
							{player.firstName} {player.lastName}
						</h1>
						<div className="pp__profile-meta">
							{player.primaryRole && (
								<span className="pp__profile-role">{player.primaryRole}</span>
							)}
							{age != null && <span className="pp__profile-age">{age} anni</span>}
							{player.nationality && <span>{player.nationality}</span>}
						</div>
					</div>
					{avgRating != null && (
						<div className={`pp__profile-rating pp__profile-rating--${avgRating >= 7 ? "green" : avgRating >= 5.5 ? "yellow" : "red"}`}>
							<span className="pp__profile-rating-value">{avgRating.toFixed(1)}</span>
							<span className="pp__profile-rating-label">Rating</span>
						</div>
					)}
				</div>

				<div className="pp__profile-details">
					{player.category && (
						<div className="pp__detail">
							<span className="pp__detail-label">Categoria</span>
							<span className="pp__detail-value">{player.category}</span>
						</div>
					)}
					{(player.dateOfBirth || player.birthDate) && (
						<div className="pp__detail">
							<span className="pp__detail-label">Data nascita</span>
							<span className="pp__detail-value">
								{moment(player.dateOfBirth || player.birthDate).format("DD/MM/YYYY")}
							</span>
						</div>
					)}
					{player.preferredFoot && (
						<div className="pp__detail">
							<span className="pp__detail-label">Piede</span>
							<span className="pp__detail-value">{player.preferredFoot}</span>
						</div>
					)}
					{player.height && (
						<div className="pp__detail">
							<span className="pp__detail-label">Altezza</span>
							<span className="pp__detail-value">{player.height} cm</span>
						</div>
					)}
					{player.weight && (
						<div className="pp__detail">
							<span className="pp__detail-label">Peso</span>
							<span className="pp__detail-value">{player.weight} kg</span>
						</div>
					)}
				</div>
			</div>

			{/* ── Season stats (from Wyscout import) ──────────── */}
			{wyscoutData && (
				<div className="pp__section">
					<div className="pp__section-title">Statistiche Stagione</div>
					<div className="pp__stats-strip">
						{[
							{label: "Partite", value: fmt(wyscoutData.matchCount, 0)},
							{label: "Minuti", value: fmt(wyscoutData.totals?.minutes, 0)},
							{label: "Gol", value: fmt(wyscoutData.totals?.goals, 0)},
							{label: "Assist", value: fmt(wyscoutData.totals?.assists, 0)},
							{label: "xG", value: fmt(wyscoutData.totals?.xG, 2)},
						].map((s, i) => (
							<div key={i} className="pp__stat">
								<span className="pp__stat-value">{s.value}</span>
								<span className="pp__stat-label">{s.label}</span>
							</div>
						))}
					</div>
					{wyscoutData.per90 && (
						<div className="pp__per90">
							<span className="pp__per90-title">Per 90 minuti</span>
							<div className="pp__per90-items">
								{[
									{label: "Gol", value: fmt(wyscoutData.per90.goals, 2)},
									{label: "Assist", value: fmt(wyscoutData.per90.assists, 2)},
									{label: "xG", value: fmt(wyscoutData.per90.xG, 2)},
									{label: "Duelli vinti", value: fmt(wyscoutData.per90.duelsWon, 1)},
									{label: "Intercetti", value: fmt(wyscoutData.per90.interceptions, 2)},
								].map((s, i) => (
									<div key={i} className="pp__per90-item">
										<span className="pp__per90-value">{s.value}</span>
										<span className="pp__per90-label">{s.label}</span>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{/* ── SMART Radar ──────────────────────────────────── */}
			{(() => {
				const smartVals = valuations.filter((v) => v.valuationType === 7);
				if (!smartVals.length) return null;
				const avgValues = {};
				const pids = ["decision_making", "concentration", "mentality", "team_play", "control", "passing", "dribbling", "finishing", "speed", "explosiveness", "stamina", "strength", "positioning", "game_reading", "off_ball_movement", "vision", "offensive_duels", "defensive_duels", "aerial_duels"];
				pids.forEach((pid) => {
					const vals = smartVals.map((v) => apiFieldsToValues(v)[pid]).filter((x) => x != null);
					avgValues[pid] = vals.length ? vals.reduce((s, x) => s + x, 0) / vals.length : null;
				});
				return (
					<div className="pp__section">
						<div className="pp__section-title">Profilo SMART</div>
						<SmartRadarChart
							values={avgValues}
							playerName={`${player?.firstName} ${player?.lastName}`}
						/>
					</div>
				);
			})()}

			{/* ── Growth trajectory ────────────────────────────── */}
			{(heightData.length >= 2 || weightData.length >= 2) && (
				<div className="pp__section">
					<div className="pp__section-title">Sviluppo Fisico</div>
					<div className="pp__growth-charts">
						{heightData.length >= 2 && (
							<div className="pp__growth-chart">
								<div className="pp__growth-chart-title">Altezza (cm)</div>
								<ResponsiveContainer width="100%" height={160}>
									<ComposedChart data={heightData} margin={{top: 5, right: 10, left: -15, bottom: 0}}>
										<CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
										<XAxis dataKey="label" tick={{fontSize: 9, fill: "#94a3b8"}} axisLine={false} tickLine={false} />
										<YAxis tick={{fontSize: 9, fill: "#94a3b8"}} axisLine={false} tickLine={false} domain={["dataMin - 2", "dataMax + 2"]} />
										<Tooltip contentStyle={{fontSize: 11, borderRadius: 6, border: "1px solid #e2e8f0"}} />
										<Area dataKey="value" fill="rgba(155,27,42,0.08)" stroke="none" />
										<Line dataKey="value" stroke="#1059bf" strokeWidth={2} dot={{r: 3, fill: "#1059bf"}} />
									</ComposedChart>
								</ResponsiveContainer>
							</div>
						)}
						{weightData.length >= 2 && (
							<div className="pp__growth-chart">
								<div className="pp__growth-chart-title">Peso (kg)</div>
								<ResponsiveContainer width="100%" height={160}>
									<ComposedChart data={weightData} margin={{top: 5, right: 10, left: -15, bottom: 0}}>
										<CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
										<XAxis dataKey="label" tick={{fontSize: 9, fill: "#94a3b8"}} axisLine={false} tickLine={false} />
										<YAxis tick={{fontSize: 9, fill: "#94a3b8"}} axisLine={false} tickLine={false} domain={["dataMin - 2", "dataMax + 2"]} />
										<Tooltip contentStyle={{fontSize: 11, borderRadius: 6, border: "1px solid #e2e8f0"}} />
										<Area dataKey="value" fill="rgba(155,27,42,0.08)" stroke="none" />
										<Line dataKey="value" stroke="#1059bf" strokeWidth={2} dot={{r: 3, fill: "#1059bf"}} />
									</ComposedChart>
								</ResponsiveContainer>
							</div>
						)}
					</div>
				</div>
			)}

			{/* ── Valuations ───────────────────────────────────── */}
			{valuations.length > 0 && (
				<div className="pp__section">
					<div className="pp__section-title">
						Valutazioni ({valuations.length})
					</div>
					<div className="pp__valuations">
						{[...valuations]
							.sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt))
							.slice(0, 5)
							.map((val, i) => {
								const rating = parseFloat(val.finalOverallRating || val.averageRating || val.rating);
								return (
									<div key={i} className="pp__valuation">
										<span className="pp__valuation-date">
											{val.date ? moment(val.date).format("DD/MM/YYYY") : "—"}
										</span>
										<span className="pp__valuation-author">
											{val.author || val.scout || val.evaluator || "—"}
										</span>
										{!isNaN(rating) && (
											<span className={`pp__valuation-rating pp__valuation-rating--${rating >= 7 ? "green" : rating >= 5.5 ? "yellow" : "red"}`}>
												{rating.toFixed(1)}
											</span>
										)}
									</div>
								);
							})}
					</div>
				</div>
			)}

			{/* ── Footer ──────────────────────────────────────── */}
			<div className="pp__footer">
				<span className="pp__footer-brand">Football Decision OS</span>
				<span className="pp__footer-date">
					Generato il {moment().format("DD/MM/YYYY")}
				</span>
			</div>
		</div>
	);
}

export default PublicPlayerPage;
